//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import React, { ReactElement } from 'react'

import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined'
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined'
import { Badge, Box, Menu, MenuItem, Typography } from '@mui/material'
import { AlertCTO } from '@shared/AlertCTO'
import { AlertLevelEnum, ObjectTypeEnum, RoleEnum } from '@shared/Enums'
import { useNavigate } from 'react-router-dom'

import { AddressPill } from '@react/components/addresses/AddressPill'
import { useOtChainState, useOtSelector } from '@react/redux/store'
import { logOT } from '@shared/loggingShared'

export default function AlertMenu(): ReactElement {
  const alertCTOs = useOtSelector((state) => state.alertState.alerts)
  const {
    userConnection: { roleEnum },
  } = useOtChainState()

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)

  const [open, setOpen] = React.useState(false)
  const [openErrors, setOpenErrors] = React.useState(false)
  const navigate = useNavigate()

  const count = alertCTOs?.filter((alertCTO) => alertCTO.alertLevel === AlertLevelEnum.REQUIRED)?.length

  const errorCount = alertCTOs?.filter((alertCTO) => alertCTO.alertLevel === AlertLevelEnum.ERROR)?.length

  const handleMenuItemClick = (_event: React.MouseEvent<HTMLElement>, alertCTO: AlertCTO): void => {
    setAnchorEl(null)
    setOpen(false)
    setOpenErrors(false)
    logOT.debug('ALERT ', alertCTO)

    if (alertCTO.sourceObjectType === ObjectTypeEnum.POOL && alertCTO.sourceObjectAddr) {
      navigate(`/pools/${alertCTO.sourceObjectAddr}`)
    }
    if (alertCTO.sourceObjectType === ObjectTypeEnum.LOAN && alertCTO.sourceObjectAddr) {
      navigate(`/loans/${alertCTO.sourceObjectAddr}`)
    }
  }
  const handleClickBox = (event: React.MouseEvent<HTMLElement>): void => {
    if (alertCTOs.length > 0) {
      setAnchorEl(event.currentTarget)
      setOpen(!open)
    }
  }
  const handleClickBoxError = (event: React.MouseEvent<HTMLElement>): void => {
    if (alertCTOs.length > 0) {
      setAnchorEl(event.currentTarget)
      setOpenErrors(!openErrors)
    }
  }

  return (
    <Box sx={{ flexDirection: 'row', columnGap: 4, display: 'flex' }}>
      {errorCount > 0 && (
        <Box onClick={handleClickBoxError}>
          <Badge color="secondary" badgeContent={errorCount}>
            <ErrorOutlineOutlinedIcon />
          </Badge>
          <Menu
            id="lock-menu"
            anchorEl={anchorEl}
            open={openErrors}
            onClose={() => {
              setOpenErrors(false)
            }}
            MenuListProps={{
              'aria-labelledby': 'lock-button',
              role: 'listbox',
            }}
          >
            {alertCTOs
              .filter((alertCTO) => alertCTO.alertLevel === AlertLevelEnum.ERROR)
              .map((alertCTO, index) => (
                <MenuItem key={`key${index}`} onClick={(event) => handleMenuItemClick(event, alertCTO)}>
                  <Box>
                    {alertCTO.txHash && (
                      <Box sx={{ justifyContent: 'center' }}>
                        <AddressPill addr={alertCTO.txHash} style={{ fontSize: 12 }} hasEtherScanLink={true} />
                      </Box>
                    )}

                    <div>
                      <Typography variant="bodyBold">{alertCTO.title}</Typography>
                    </div>
                    {alertCTO.description !== '' && (
                      <div>
                        <Typography variant="bodyMuted">{alertCTO.description}</Typography>
                      </div>
                    )}
                  </Box>
                </MenuItem>
              ))}
          </Menu>
        </Box>
      )}
      {roleEnum !== RoleEnum.LENDER && (
        <Box onClick={handleClickBox}>
          <Badge color="secondary" badgeContent={count}>
            <NotificationsOutlinedIcon />
          </Badge>
          <Menu
            id="lock-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={() => {
              setOpen(false)
            }}
            MenuListProps={{
              'aria-labelledby': 'lock-button',
              role: 'listbox',
            }}
          >
            {alertCTOs
              .filter((alertCTO) => alertCTO.alertLevel === AlertLevelEnum.REQUIRED)
              .map((alertCTO, index) => {
                return (
                  <MenuItem key={`key${index}`} onClick={(event) => handleMenuItemClick(event, alertCTO)}>
                    <Box>
                      <div>
                        <Typography variant="bodyBold">{alertCTO.title}</Typography>
                      </div>
                      {alertCTO.description !== '' && (
                        <div>
                          <Typography variant="bodyMuted">{alertCTO.description}</Typography>
                        </div>
                      )}
                    </Box>
                  </MenuItem>
                )
              })}
          </Menu>
        </Box>
      )}
    </Box>
  )
}
